import { LocationCoords } from '../types/fare';

/**
 * Decode Google Maps encoded polyline string
 * Based on: https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
export function decodePolyline(encoded: string): LocationCoords[] {
  if (!encoded) return [];

  const points: LocationCoords[] = [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;

  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push({
      lat: lat / 1e5,
      lng: lng / 1e5
    });
  }

  return points;
}

/**
 * Calculate the midpoint of a polyline path
 * Useful for positioning overlays on routes
 */
export function getPolylineMidpoint(path: LocationCoords[]): LocationCoords {
  if (path.length === 0) {
    return { lat: 0, lng: 0 };
  }

  if (path.length === 1) {
    return path[0];
  }

  // Get point at approximately 30% along the route for better visibility
  const index = Math.floor(path.length * 0.3);
  return path[index];
}

/**
 * Calculate bounds that fit all coordinates
 * Returns bounds object for Google Maps fitBounds
 */
export function calculateBounds(
  coordinates: LocationCoords[]
): google.maps.LatLngBounds | null {
  if (coordinates.length === 0) return null;

  const bounds = new google.maps.LatLngBounds();
  coordinates.forEach(coord => {
    bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
  });

  return bounds;
}

/**
 * Calculate bounds for multiple polylines
 */
export function calculateBoundsForRoutes(
  polylines: string[]
): google.maps.LatLngBounds | null {
  const allCoords: LocationCoords[] = [];

  polylines.forEach(polyline => {
    if (polyline) {
      const decoded = decodePolyline(polyline);
      allCoords.push(...decoded);
    }
  });

  return calculateBounds(allCoords);
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in kilometers
 */
export function calculateDistance(
  point1: LocationCoords,
  point2: LocationCoords
): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRadians(point2.lat - point1.lat);
  const dLng = toRadians(point2.lng - point1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.lat)) *
    Math.cos(toRadians(point2.lat)) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert LocationCoords to google.maps.LatLngLiteral
 */
export function toLatLngLiteral(coords: LocationCoords): google.maps.LatLngLiteral {
  return { lat: coords.lat, lng: coords.lng };
}

/**
 * Convert google.maps.LatLng to LocationCoords
 */
export function fromLatLng(latLng: google.maps.LatLng): LocationCoords {
  return {
    lat: latLng.lat(),
    lng: latLng.lng()
  };
}

